import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Modal, Nav, Spinner, Tab } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  listEcoDictionaryItems,
  resolveEcoDictionaryItem,
  selectEcoById,
  updateEcoById,
  upsertEcoDictionaryItem,
} from 'services/ecodoppler';
import 'styles/laudo-base.css';
import CampoMedida from 'components/laudos/CampoMedida';
import ListaDicionario from 'components/laudos/ListaDicionario';
import ListaDicionarioComposta from 'components/laudos/ListaDicionarioComposta';
import useSWR, { mutate } from 'swr';

const bottomTabList = [
  { key: 'dados-pessoais', label: 'Dados Pessoais' },
  { key: 'funcao-sistolica', label: 'Função Sist. do VE' },
  { key: 'funcao-diastolica', label: 'Função Diast. do VE' },
  { key: 'atrios', label: 'Átrio Esq. e Átrio Direito' },
  { key: 'ventriculo-aorta', label: 'Ventrículo Direito e Aorta' },
  { key: 'valv-mitral', label: 'Valv. Mitral' },
  { key: 'valv-aortica', label: 'Valv. Aórtica' },
  { key: 'valv-tricuspide', label: 'Valv. Tricúspide' },
  { key: 'valv-pulmonar', label: 'Valv. Pulmonar' },
  { key: 'pericardio', label: 'Pericárdio' },
  { key: 'comentarios', label: 'Comentários' },
  { key: 'observacoes', label: 'Observações' },
];

const tabList = [...bottomTabList];

const tabHeadingMap = {
  'dados-pessoais': 'Dados Pessoais',
  'funcao-sistolica': 'Diâmetros e Função Sistólica do Ventrículo Esquerdo',
  'funcao-diastolica': 'Avaliação da Função Diastólica do Ventrículo Esquerdo',
  atrios: 'Átrio Esquerdo e Átrio Direito',
  'ventriculo-aorta': 'Ventrículo Direito e Aorta',
  'valv-mitral': 'Valva Mitral',
  'valv-aortica': 'Valva Aórtica',
  'valv-tricuspide': 'Valva Tricúspide',
  'valv-pulmonar': 'Valva Pulmonar',
  pericardio: 'Pericárdio',
  comentarios: 'Comentários',
  observacoes: 'Observações',
};

// --- Navegação por ENTER entre campos de medida, listas de dicionário e abas ---
// Replica o comportamento do legado (Delphi 7): ENTER percorre tanto os
// CampoMedida editáveis quanto as ListaDicionario/ListaDicionarioComposta,
// na ordem visual real da tela. Campos calculados/read-only (ex: relacaoEA,
// relacaoSPP, fracaoEncurtamento) ficam de fora, pois nunca recebem foco.
//
// Cada entrada é tipada:
//   { type: 'field', name: 'SEPTO' }  -> CampoMedida
//   { type: 'list',  name: 'achados' } -> ListaDicionario / ListaDicionarioComposta
const fieldNavigationConfig = {
  'funcao-sistolica': [
    { type: 'field', name: 'SEPTO' },
    { type: 'field', name: 'PPVE' },
    { type: 'field', name: 'DD' },
    { type: 'field', name: 'DS' },
    { type: 'field', name: 'MASSAVE' },
    { type: 'field', name: 'TEVE_NEW' },
    { type: 'field', name: 'EDC_VE' },
    { type: 'field', name: 'DPDT' },
    { type: 'field', name: 'FE' },
    { type: 'list', name: 'achados' },
    { type: 'list', name: 'contraRegiao' },
  ],
  'funcao-diastolica': [
    { type: 'field', name: 'VEM' },
    { type: 'field', name: 'VAM' },
    { type: 'field', name: 'TRIVVE' },
    { type: 'field', name: 'TDM' },
    { type: 'field', name: 'DOAM' },
    { type: 'field', name: 'DOAVP' },
    { type: 'field', name: 'VOAVP' },
    { type: 'field', name: 'VTIAO' },
    { type: 'field', name: 'AAD' },
    { type: 'list', name: 'avDiast' },
  ],
  atrios: [
    { type: 'field', name: 'DAE' },
    { type: 'field', name: 'VTIVSVE4' },
    { type: 'list', name: 'atrioEsq' },
    { type: 'list', name: 'atrioDir' },
  ],
  'ventriculo-aorta': [
    { type: 'field', name: 'VA' },
    { type: 'list', name: 'ventriDir' },
    { type: 'field', name: 'VTIVSVE1' },
    { type: 'list', name: 'aorta' },
  ],
  'valv-mitral': [
    { type: 'field', name: 'AVM' },
    { type: 'field', name: 'VTIVSVE3' },
    { type: 'field', name: 'GMVM' },
    { type: 'field', name: 'ARIMCLL' },
    { type: 'field', name: 'AAE' },
    { type: 'field', name: 'ORIFICIO_VM' },
    { type: 'field', name: 'VOLUME_VM' },
    { type: 'field', name: 'VTIVSVE2' },
    { type: 'list', name: 'valvaMitral' },
    { type: 'list', name: 'refVM' },
    { type: 'field', name: 'GMPM' },
    { type: 'list', name: 'refluxoPM' },
  ],
  'valv-aortica': [
    { type: 'field', name: 'AREA_AO' },
    { type: 'field', name: 'VTIMITRAL2' },
    { type: 'field', name: 'GMVSVE' },
    { type: 'field', name: 'GMVAO' },
    { type: 'field', name: 'DIAOCLL' },
    { type: 'field', name: 'DAVAO' },
    { type: 'field', name: 'VTIRM' },
    { type: 'list', name: 'valvaAO' },
    { type: 'list', name: 'refVAO' },
    { type: 'field', name: 'GMPAO' },
    { type: 'field', name: 'DVSVE' },
    { type: 'list', name: 'refluxoPAO' },
  ],
  'valv-tricuspide': [
    { type: 'field', name: 'GPIT' },
    { type: 'field', name: 'GMVT' },
    { type: 'field', name: 'RCIM' },
    { type: 'list', name: 'valvaTricu' },
    { type: 'list', name: 'refVT' },
    { type: 'field', name: 'GMPT' },
    { type: 'list', name: 'refluxoPT' },
  ],
  'valv-pulmonar': [
    { type: 'field', name: 'DAVP' },
    { type: 'field', name: 'VPRT' },
    { type: 'field', name: 'GMVP' },
    { type: 'list', name: 'valvaPulmo' },
    { type: 'list', name: 'refVP' },
    { type: 'field', name: 'GMPP' },
    { type: 'list', name: 'refluxoPP' },
  ],
};

// Ordem das abas que possuem campos/listas navegáveis por ENTER.
const navigableTabOrder = Object.keys(fieldNavigationConfig);

// Aba para a qual o ENTER deve voltar ao terminar o último item da
// última aba navegável (fecha o ciclo de preenchimento sequencial).
const FIRST_TAB_KEY = 'dados-pessoais';

function useFieldNavigation({ onAdvanceTab }) {
  // refsRef.current[tabKey][itemName] = { current } (CampoMedida)
  //   ou a instância do componente de lista (via forwardRef/useImperativeHandle)
  const refsRef = useRef({});

  const getEntryRef = useCallback((tabKey, itemName, type) => {
    if (!refsRef.current[tabKey]) {
      refsRef.current[tabKey] = {};
    }
    if (!refsRef.current[tabKey][itemName]) {
      // CampoMedida usa objeto { current } simples (ref de elemento DOM).
      // Listas usam ref de instância de componente (forwardRef), então o
      // próprio objeto retornado pelo React já serve como "current" — não
      // precisamos encapsular, mas mantemos o mesmo formato { current }
      // para uniformizar o acesso em focusEntry().
      refsRef.current[tabKey][itemName] = { current: null, type };
    }
    return refsRef.current[tabKey][itemName];
  }, []);

  const focusEntry = useCallback((tabKey, itemName) => {
    // requestAnimationFrame garante que o item já esteja montado/visível,
    // o que importa especialmente ao trocar de aba antes de focar.
    requestAnimationFrame(() => {
      const entry = refsRef.current[tabKey]?.[itemName];
      if (!entry?.current) return;

      if (entry.type === 'list') {
        entry.current.focusFirstRow?.();
      } else {
        entry.current.focus?.();
      }
    });
  }, []);

  const handleEnterOnEntry = useCallback(
    (tabKey, itemName) => {
      const order = fieldNavigationConfig[tabKey] || [];
      const currentIndex = order.findIndex((entry) => entry.name === itemName);
      const nextEntry = currentIndex >= 0 ? order[currentIndex + 1] : undefined;

      if (nextEntry) {
        focusEntry(tabKey, nextEntry.name);
        return;
      }

      // Último item da aba atual: decide para qual aba avançar.
      const tabIndex = navigableTabOrder.indexOf(tabKey);
      const isLastNavigableTab = tabIndex === navigableTabOrder.length - 1;
      const nextTabKey = isLastNavigableTab ? FIRST_TAB_KEY : navigableTabOrder[tabIndex + 1];

      if (!nextTabKey) return;

      onAdvanceTab?.(nextTabKey, () => {
        const firstEntryOfNextTab = fieldNavigationConfig[nextTabKey]?.[0];
        if (firstEntryOfNextTab) {
          focusEntry(nextTabKey, firstEntryOfNextTab.name);
        }
        // Se nextTabKey for 'dados-pessoais' (sem item navegável),
        // apenas a troca de aba ocorre, sem foco adicional.
      });
    },
    [focusEntry, onAdvanceTab]
  );

  // Uso: <CampoMedida ... {...getFieldProps('funcao-sistolica', 'SEPTO')} />
  const getFieldProps = useCallback(
    (tabKey, fieldName) => ({
      ref: getEntryRef(tabKey, fieldName, 'field'),
      onEnterPress: () => handleEnterOnEntry(tabKey, fieldName),
    }),
    [getEntryRef, handleEnterOnEntry]
  );

  // Uso: <ListaDicionario ... {...getListProps('funcao-sistolica', 'achados')} />
  const getListProps = useCallback(
    (tabKey, listName) => ({
      ref: getEntryRef(tabKey, listName, 'list'),
      onEnterPress: () => handleEnterOnEntry(tabKey, listName),
    }),
    [getEntryRef, handleEnterOnEntry]
  );

  return { getFieldProps, getListProps };
}

const listDictionaryConfig = {
  obs: { group: 'obs', codeKey: 'COD' },
  achados: { group: 'achados', codeKey: 'COD' },
  contraRegiao: {
    group: 'contraSeg',
    codeKey: 'COD',
    textKey: 'CONTRASEG',
    codeLabel: 'Cod',
    textLabel: 'Contração Segmentar',
    extraFields: [
      {
        codeKey: 'CODREGIAO',
        group: 'regiao',
        codeLabel: 'Reg',
        textKey: 'regiaoDescricao',
        textLabel: 'Região',
      },
    ],
  },
  avDiast: { group: 'avDiast', codeKey: 'COD' },
  atrioEsq: { group: 'atrioEsq', codeKey: 'COD' },
  atrioDir: { group: 'atrioDir', codeKey: 'COD' },
  valvaMitral: { group: 'valvaMitral', codeKey: 'COD' },
  valvaTricu: { group: 'valvaTricu', codeKey: 'COD' },
  valvaPulmo: { group: 'valvaPulmo', codeKey: 'COD' },
  valvaAO: { group: 'valvaAO', codeKey: 'COD' },
  aorta: { group: 'aorta', codeKey: 'COD' },
  veiaCavaInf: { group: 'veiaCavaInf', codeKey: 'COD' },
  ventriDir: { group: 'ventriDir', codeKey: 'COD' },
  pericardio: { group: 'pericardio', codeKey: 'COD' },
  refVM: { group: 'refVM', codeKey: 'COD' },
  refVAO: { group: 'refVAO', codeKey: 'COD' },
  refVT: { group: 'refVT', codeKey: 'COD' },
  refVP: { group: 'refVP', codeKey: 'COD' },
  refluxoPM: {
    group: 'refluxoPM',
    codeKey: 'COD',
    textKey: 'refluxoPM',
    codeLabel: 'Cod',
    textLabel: 'Prótese Mitral',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
  refluxoPAO: {
    group: 'refluxoPAO',
    codeKey: 'COD',
    textKey: 'refluxoPAO',
    codeLabel: 'Cod',
    textLabel: 'Prótese Aórtica',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
  refluxoPT: {
    group: 'refluxoPT',
    codeKey: 'COD',
    textKey: 'refluxoPT',
    codeLabel: 'Cod',
    textLabel: 'Prótese Tricúspide',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
  refluxoPP: {
    group: 'refluxoPP',
    codeKey: 'COD',
    textKey: 'refluxoPP',
    codeLabel: 'Cod',
    textLabel: 'Prótese Pulmonar',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
  valvulopatia: {
    group: 'valvulopatia',
    codeKey: 'COD',
    textKey: 'valvulopatia',
    codeLabel: 'Cod',
    textLabel: 'Valvulopatia',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
      {
        codeKey: 'CODPREDOMI',
        group: 'predominancia',
        codeLabel: 'Pred',
        textKey: 'predominanciaDescricao',
        textLabel: 'Predominância',
      },
    ],
  },
  protese: {
    group: 'protese',
    codeKey: 'COD',
    textKey: 'protese',
    codeLabel: 'Cod',
    textLabel: 'Prótese',
    extraFields: [
      {
        codeKey: 'CDODESCRICAO',
        group: 'descricao',
        codeLabel: 'Desc',
        textKey: 'descricaoDescricao',
        textLabel: 'Descrição',
      },
      {
        codeKey: 'CODPREDOMI',
        group: 'predominancia',
        codeLabel: 'Pred',
        textKey: 'predominanciaDescricao',
        textLabel: 'Predominância',
      },
    ],
  },
  conclusoes: {
    group: 'conclusoes',
    codeKey: 'COD',
    textKey: 'conclusoes',
    codeLabel: 'Cod',
    textLabel: 'Conclusão',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
};

const compositeListKeys = new Set(
  Object.entries(listDictionaryConfig)
    .filter(([, config]) => Array.isArray(config.extraFields) && config.extraFields.length > 0)
    .map(([key]) => key)
);

const dictionaryGroupLabels = {
  obs: 'Obs',
  achados: 'Achados VE',
  contraSeg: 'Contração Segmentar',
  avDiast: 'Avaliação Diastólica',
  atrioEsq: 'Átrio Esquerdo',
  atrioDir: 'Átrio Direito',
  valvaMitral: 'Valva Mitral',
  valvaTricu: 'Valva Tricúspide',
  valvaPulmo: 'Valva Pulmonar',
  valvaAO: 'Valva Aórtica',
  aorta: 'Aorta',
  veiaCavaInf: 'Veia Cava Inferior',
  ventriDir: 'Ventrículo Direito',
  pericardio: 'Pericárdio',
  refVM: 'Refluxo Mitral',
  refVAO: 'Refluxo Aórtico',
  refVT: 'Refluxo Tricúspide',
  refVP: 'Refluxo Pulmonar',
  refluxoPM: 'Prótese Mitral',
  refluxoPAO: 'Prótese Aórtica',
  refluxoPT: 'Prótese Tricúspide',
  refluxoPP: 'Prótese Pulmonar',
  valvulopatia: 'Valvulopatias',
  protese: 'Próteses',
  conclusoes: 'Conclusões',
};

const dictionaryTabList = Array.from(
  new Set(Object.values(listDictionaryConfig).map((item) => item.group))
).map((group) => ({
  key: group,
  label: dictionaryGroupLabels[group] || group,
}));

function AutoSaveCloudIndicator({ status, className = '', compact = false }) {
  if (status === 'idle') return null;

  const isSaving = status === 'saving';
  const isSaved = status === 'saved';
  const isError = status === 'error';

  const text = isSaving ? 'Salvando...' : isSaved ? 'Tudo salvo' : 'Falha ao salvar';
  const rootClass = `legacy-cloud-save ${className} ${isSaved ? 'is-saved' : ''} ${
    isError ? 'is-error' : ''
  }`;

  return (
    <span className={rootClass} role="status" aria-live="polite">
      <span className="legacy-cloud-save-icon-wrap" aria-hidden="true">
        <svg
          className="legacy-cloud-save-icon"
          viewBox="0 0 24 24"
          focusable="false"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M19 18a4 4 0 0 0 .6-7.95A6.5 6.5 0 0 0 7.06 8.1 4.75 4.75 0 0 0 7 17.5h12z" />
        </svg>
        {isSaving ? <span className="legacy-cloud-save-spinner" /> : null}
        {isSaved ? (
          <svg
            className="legacy-cloud-save-check"
            viewBox="0 0 14 14"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M5.4 10.4 2.1 7.2l1.1-1.2 2.2 2.2L10.8 3l1.1 1.1z" />
          </svg>
        ) : null}
      </span>
      {!compact ? <span className="legacy-cloud-save-text">{text}</span> : null}
    </span>
  );
}

export default function FormEcodoppler() {
  const history = useHistory();
  const listKeys = [
    'obs',
    'achados',
    'contraRegiao',
    'avDiast',
    'atrioEsq',
    'atrioDir',
    'valvaMitral',
    'valvaTricu',
    'valvaPulmo',
    'valvaAO',
    'aorta',
    'veiaCavaInf',
    'ventriDir',
    'pericardio',
    'refVM',
    'refVAO',
    'refVT',
    'refVP',
    'refluxoPM',
    'refluxoPAO',
    'refluxoPT',
    'refluxoPP',
    'valvulopatia',
    'protese',
    'conclusoes',
  ];

  const { id } = useParams();
  const {
    data: eco,
    isLoading,
    error,
  } = useSWR(id ? ['ecodoppler', id] : null, () => selectEcoById(id));
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autosaving, setAutosaving] = useState(false);
  const [autoSaveUiState, setAutoSaveUiState] = useState('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('dados-pessoais');
  const [resolvingRows, setResolvingRows] = useState({});
  const dictionaryCacheRef = useRef(new Map());
  const autoSaveStatusTimeoutRef = useRef(null);
  const typingAutoSaveTimeoutRef = useRef(null);
  const [showDictionaryModal, setShowDictionaryModal] = useState(false);
  const [activeDictionaryTab, setActiveDictionaryTab] = useState(dictionaryTabList[0]?.key || '');
  const [dictionaryData, setDictionaryData] = useState({});
  const [dictionaryLoading, setDictionaryLoading] = useState({});
  const [dictionaryDraft, setDictionaryDraft] = useState({});
  const [savingDictionaryKey, setSavingDictionaryKey] = useState('');

  const resolveDictionaryValue = useCallback(async ({ group, code }) => {
    const normalizedCode = String(code || '').trim();
    const normalizedGroup = String(group || '').trim();
    if (!normalizedCode || !normalizedGroup) return null;

    const cacheKey = `${normalizedGroup}|${normalizedCode}`;
    if (dictionaryCacheRef.current.has(cacheKey)) {
      return dictionaryCacheRef.current.get(cacheKey);
    }

    const resolved = await resolveEcoDictionaryItem({
      grupo: normalizedGroup,
      codigo: normalizedCode,
    });

    if (resolved) {
      dictionaryCacheRef.current.set(cacheKey, resolved);
    }

    return resolved;
  }, []);

  const getCompositeColumns = useCallback((listKey, keyName) => {
    const listConfig = listDictionaryConfig[listKey] || {};
    const baseColumn = {
      codeKey: listConfig.codeKey || 'COD',
      codeLabel: listConfig.codeLabel || 'Cod',
      textKey: listConfig.textKey || keyName,
      textLabel: listConfig.textLabel || keyName,
    };

    return [baseColumn, ...(listConfig.extraFields || [])];
  }, []);

  useEffect(() => {
    if (!eco) return;

    const normalized = { ...eco };
    listKeys.forEach((key) => {
      normalized[key] = Array.isArray(eco[key]) ? eco[key].map((item) => ({ ...item })) : [];
    });
    setFormData(normalized);
    setHasUnsavedChanges(false);
  }, [eco]);

  useEffect(() => {
    if (!formData) return;

    let isCancelled = false;

    const hydrateCompositeDescriptions = async () => {
      const updates = [];

      for (const listKey of compositeListKeys) {
        const listConfig = listDictionaryConfig[listKey];
        const items = Array.isArray(formData[listKey]) ? formData[listKey] : [];

        for (let index = 0; index < items.length; index += 1) {
          const item = items[index] || {};
          const columns = getCompositeColumns(listKey, listConfig.textKey || 'DESCRICAO');
          const patch = {};
          let hasPatch = false;

          for (const column of columns) {
            const codeValue = String(item?.[column.codeKey] || '').trim();
            const currentText = String(item?.[column.textKey] || '').trim();

            if (!codeValue || currentText) continue;

            const resolved = await resolveDictionaryValue({
              group:
                column.codeKey === (listConfig.codeKey || 'COD')
                  ? listConfig.group || listKey
                  : column.group,
              code: codeValue,
            });

            if (resolved?.descricao) {
              patch[column.textKey] = resolved.descricao;
              hasPatch = true;
            }
          }

          if (hasPatch) {
            updates.push({ listKey, index, patch });
          }
        }
      }

      if (isCancelled || updates.length === 0) return;

      setFormData((prev) => {
        if (!prev) return prev;
        const next = { ...prev };

        updates.forEach(({ listKey, index, patch }) => {
          const list = Array.isArray(next[listKey]) ? [...next[listKey]] : [];
          if (!list[index]) return;
          list[index] = { ...list[index], ...patch };
          next[listKey] = list;
        });

        return next;
      });
    };

    hydrateCompositeDescriptions();

    return () => {
      isCancelled = true;
    };
  }, [formData, getCompositeColumns, resolveDictionaryValue]);

  useEffect(
    () => () => {
      if (autoSaveStatusTimeoutRef.current) {
        clearTimeout(autoSaveStatusTimeoutRef.current);
      }

      if (typingAutoSaveTimeoutRef.current) {
        clearTimeout(typingAutoSaveTimeoutRef.current);
      }
    },
    []
  );

  const persistEco = useCallback(
    async ({ silent = false, revalidate = true } = {}) => {
      if (!id || !formData) {
        if (!silent) toast.warn('Erro de validação!');
        return false;
      }

      try {
        if (silent) {
          setAutosaving(true);
          setAutoSaveUiState('saving');
        } else {
          setSaving(true);
        }

        const res = await updateEcoById(id, formData);

        if (revalidate) {
          await mutate(['ecodoppler', id]);
        }

        if (!silent) {
          toast.success(res?.message || 'Laudo atualizado.');
        } else {
          if (autoSaveStatusTimeoutRef.current) {
            clearTimeout(autoSaveStatusTimeoutRef.current);
          }
          setAutoSaveUiState('saved');
          autoSaveStatusTimeoutRef.current = setTimeout(() => {
            setAutoSaveUiState('idle');
          }, 2500);
        }

        setHasUnsavedChanges(false);
        return true;
      } catch (err) {
        if (silent) {
          toast.error(err.response?.data?.message || 'Falha no auto-save ao trocar de aba.');
          if (autoSaveStatusTimeoutRef.current) {
            clearTimeout(autoSaveStatusTimeoutRef.current);
          }
          setAutoSaveUiState('error');
          autoSaveStatusTimeoutRef.current = setTimeout(() => {
            setAutoSaveUiState('idle');
          }, 4000);
        } else {
          toast.error(err.response?.data?.message || 'Erro ao gravar.');
        }
        return false;
      } finally {
        setSaving(false);
        setAutosaving(false);
      }
    },
    [id, formData]
  );

  const handleGravar = async () => {
    await persistEco();
  };

  const handleAnexar = () => toast.info('em desenvolvimento...');

  const handleVisualizar = () => {
    if (!id) {
      toast.warn('Erro de validação!');
      return;
    }

    history.push(`/visualizacao-ecodoppler/${id}`);
  };

  useEffect(() => {
    if (!id || !formData || !hasUnsavedChanges || saving || autosaving) {
      if (typingAutoSaveTimeoutRef.current) {
        clearTimeout(typingAutoSaveTimeoutRef.current);
      }
      return;
    }

    // Background auto-save after a short typing pause.
    typingAutoSaveTimeoutRef.current = setTimeout(() => {
      persistEco({ silent: true, revalidate: false });
    }, 1200);

    return () => {
      if (typingAutoSaveTimeoutRef.current) {
        clearTimeout(typingAutoSaveTimeoutRef.current);
      }
    };
  }, [id, formData, hasUnsavedChanges, saving, autosaving, persistEco]);

  const setField = (field, value) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const setListField = (listKey, keyName, index, value) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [keyName]: value };
      return { ...prev, [listKey]: list };
    });
  };

  const setListFields = (listKey, index, patch) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], ...patch };
      return { ...prev, [listKey]: list };
    });
  };

  const addListItem = (listKey, keyName, codeKey = 'COD') => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      list.push({ [codeKey]: '', [keyName]: '' });
      return { ...prev, [listKey]: list };
    });
  };

  const addCompositeListItem = (listKey, keyName) => {
    const columns = getCompositeColumns(listKey, keyName);

    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      const newItem = {};

      columns.forEach((column) => {
        newItem[column.codeKey] = '';
        newItem[column.textKey] = '';
      });

      list.push(newItem);
      return { ...prev, [listKey]: list };
    });
  };

  const removeListItem = (listKey, index) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list.splice(index, 1);
      return { ...prev, [listKey]: list };
    });
  };

  const handleResolveListCode = async (listKey, keyName, targetCodeKey, index, codeValue) => {
    const listConfig = listDictionaryConfig[listKey] || {};
    const codeKey = listConfig.codeKey || 'COD';
    const extraFields = Array.isArray(listConfig.extraFields) ? listConfig.extraFields : [];
    const fieldConfigs = [
      {
        codeKey,
        group: listConfig.group || listKey,
        textKey: listConfig.textKey || keyName,
      },
      ...extraFields,
    ];
    const normalizedCode = String(codeValue || '').trim();
    const resolvingKey = `${listKey}:${index}`;
    const currentItem = formData?.[listKey]?.[index] || {};
    const draftItem = { ...currentItem, [targetCodeKey]: normalizedCode };

    if (!fieldConfigs.some((field) => String(draftItem[field.codeKey] || '').trim())) {
      const clearedPatch = fieldConfigs.reduce(
        (patch, field) => ({
          ...patch,
          [field.codeKey]: '',
          [field.textKey]: '',
        }),
        {}
      );
      setListFields(listKey, index, clearedPatch);
      return;
    }

    setResolvingRows((prev) => ({ ...prev, [resolvingKey]: true }));

    try {
      const nextPatch = {};

      for (const field of fieldConfigs) {
        const fieldCode = String(draftItem[field.codeKey] || '').trim();

        if (!fieldCode) {
          nextPatch[field.codeKey] = '';
          nextPatch[field.textKey] = '';
          continue;
        }

        const resolved = await resolveDictionaryValue({
          group: field.group,
          code: fieldCode,
        });
        nextPatch[field.codeKey] = resolved?.codigo || fieldCode;
        nextPatch[field.textKey] = resolved?.descricao || '';

        if (!resolved?.descricao && field.codeKey === targetCodeKey) {
          toast.warn(`Código ${fieldCode} não encontrado no dicionário.`);
        }
      }

      setListFields(listKey, index, nextPatch);
    } catch (error) {
      setListFields(listKey, index, { [targetCodeKey]: normalizedCode });
      toast.error(
        error?.response?.data?.message || 'Erro ao consultar o dicionário do ecodoppler.'
      );
    } finally {
      setResolvingRows((prev) => {
        const next = { ...prev };
        delete next[resolvingKey];
        return next;
      });
    }
  };

  const getListHandlers = (listKey, keyName = 'DESCRICAO', customCodeKey) => {
    const listConfig = listDictionaryConfig[listKey] || {};
    const codeKey = customCodeKey || listConfig.codeKey || 'COD';

    return {
      editable: true,
      keyName,
      codeKey,
      onChangeItem: (index, value) => setListField(listKey, keyName, index, value),
      onChangeCode: (index, value, targetCodeKey = codeKey) =>
        setListField(listKey, targetCodeKey, index, value),
      onResolveCode: (index, value, targetCodeKey = codeKey) =>
        handleResolveListCode(listKey, keyName, targetCodeKey, index, value),
      onAddItem: () => addListItem(listKey, keyName, codeKey),
      onRemoveItem: (index) => removeListItem(listKey, index),
      resolvingKeySet: new Set(
        Object.keys(resolvingRows)
          .filter((key) => key.startsWith(`${listKey}:`))
          .map((key) => key.split(':')[1])
      ),
    };
  };

  const getCompositeListHandlers = (listKey, keyName) => {
    const columns = getCompositeColumns(listKey, keyName);
    const primaryCodeKey = columns[0]?.codeKey || 'COD';

    return {
      editable: true,
      columns,
      onChangeCode: (index, value, targetCodeKey = primaryCodeKey) =>
        setListField(listKey, targetCodeKey, index, value),
      onResolveCode: (index, value, targetCodeKey = primaryCodeKey) =>
        handleResolveListCode(listKey, keyName, targetCodeKey, index, value),
      onAddItem: () => addCompositeListItem(listKey, keyName),
      onRemoveItem: (index) => removeListItem(listKey, index),
      resolvingKeySet: new Set(
        Object.keys(resolvingRows)
          .filter((key) => key.startsWith(`${listKey}:`))
          .map((key) => key.split(':')[1])
      ),
    };
  };

  const loadDictionaryTab = async (groupKey, { force = false } = {}) => {
    if (!groupKey) return;
    if (!force && Array.isArray(dictionaryData[groupKey])) return;

    setDictionaryLoading((prev) => ({ ...prev, [groupKey]: true }));
    try {
      const data = await listEcoDictionaryItems({ grupo: groupKey });
      setDictionaryData((prev) => ({ ...prev, [groupKey]: data }));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao carregar dicionário.');
    } finally {
      setDictionaryLoading((prev) => ({ ...prev, [groupKey]: false }));
    }
  };

  const handleOpenDictionaryModal = () => {
    setShowDictionaryModal(true);
    loadDictionaryTab(activeDictionaryTab);
  };

  const handleChangeDictionaryCell = (groupKey, index, field, value) => {
    setDictionaryData((prev) => {
      const list = Array.isArray(prev[groupKey]) ? [...prev[groupKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [groupKey]: list };
    });
  };

  const handleSaveDictionaryRow = async (groupKey, row) => {
    const codigo = String(row?.codigo || '').trim();
    const descricao = String(row?.descricao || '').trim();
    if (!codigo || !descricao) {
      toast.warn('Código e descrição são obrigatórios.');
      return;
    }

    const saveKey = `${groupKey}:${codigo}`;
    try {
      setSavingDictionaryKey(saveKey);
      await upsertEcoDictionaryItem({ grupo: groupKey, codigo, descricao });
      toast.success('Dicionário atualizado.');
      await loadDictionaryTab(groupKey, { force: true });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao salvar item do dicionário.');
    } finally {
      setSavingDictionaryKey('');
    }
  };

  const handleSaveDictionaryDraft = async (groupKey) => {
    const draft = dictionaryDraft[groupKey] || { codigo: '', descricao: '' };
    const codigo = String(draft.codigo || '').trim();
    const descricao = String(draft.descricao || '').trim();

    if (!codigo || !descricao) {
      toast.warn('Informe código e descrição para incluir novo item.');
      return;
    }

    const saveKey = `${groupKey}:${codigo}`;
    try {
      setSavingDictionaryKey(saveKey);
      await upsertEcoDictionaryItem({ grupo: groupKey, codigo, descricao });
      setDictionaryDraft((prev) => ({
        ...prev,
        [groupKey]: { codigo: '', descricao: '' },
      }));
      toast.success('Novo item incluído no dicionário.');
      await loadDictionaryTab(groupKey, { force: true });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao incluir item no dicionário.');
    } finally {
      setSavingDictionaryKey('');
    }
  };

  const handleTabSelect = async (nextTab) => {
    const targetTab = nextTab || activeTab;
    if (targetTab === activeTab) return;

    if (hasUnsavedChanges && !saving && !autosaving) {
      const saved = await persistEco({ silent: true, revalidate: false });
      if (!saved) return;
    }

    setActiveTab(targetTab);
  };

  // Navegação por ENTER entre campos de medida (e entre abas, ao final de cada uma).
  // Reaproveita handleTabSelect para manter o autosave existente ao trocar de aba.
  const { getFieldProps, getListProps } = useFieldNavigation({
    onAdvanceTab: async (nextTabKey, afterTabChange) => {
      await handleTabSelect(nextTabKey);
      afterTabChange();
    },
  });

  const renderTabContent = (key) => {
    if (!formData) return null;

    switch (key) {
      case 'dados-pessoais':
        return (
          <>
            <div className="row g-3 legacy-panel-grid">
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Paciente</div>
                  <div className="legacy-field-value">
                    {formData.CODPAC} | {formData.NOMEPAC}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Convênio</div>
                  <div className="legacy-field-value">{formData.SIGLACONV || '-'}</div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Médico Solicitou</div>
                  <div className="legacy-field-value">{formData.Solicitou || '-'}</div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Médico Realizou</div>
                  <div className="legacy-field-value">{formData.Realizou || '-'}</div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Data do Exame</div>
                  <div className="legacy-field-value">{formData.dataEcoFmt || '-'}</div>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Tipo do Laudo</div>
                  <div className="legacy-radio-row d-flex align-items-center gap-3 flex-wrap">
                    <Form.Check inline type="radio" label="Normal" checked readOnly />
                    <Form.Check inline type="radio" label="Alterado" readOnly />
                  </div>
                </div>
              </div>
            </div>

            <ListaDicionario
              title="Cod Ecocardiograma"
              items={formData.obs}
              {...getListHandlers('obs')}
            />
          </>
        );
      case 'funcao-sistolica':
        return (
          <>
            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Septo"
                  unit="cm"
                  value={formData.SEPTO}
                  editable
                  onChange={(v) => setField('SEPTO', v)}
                  {...getFieldProps('funcao-sistolica', 'SEPTO')}
                />
                <CampoMedida
                  label="PP VE"
                  unit="cm"
                  value={formData.PPVE}
                  editable
                  onChange={(v) => setField('PPVE', v)}
                  {...getFieldProps('funcao-sistolica', 'PPVE')}
                />
                <CampoMedida
                  label="Relação Septo/PP VE"
                  unit="cm"
                  value={formData.relacaoSPP}
                  editable={false}
                  onChange={(v) => setField('relacaoSPP', v)}
                />
                <CampoMedida
                  label="Diâmetro Diastólico"
                  unit="cm"
                  value={formData.DD}
                  editable
                  onChange={(v) => setField('DD', v)}
                  {...getFieldProps('funcao-sistolica', 'DD')}
                />
                <CampoMedida
                  label="Diâmetro Sistólico"
                  unit="cm"
                  value={formData.DS}
                  editable
                  onChange={(v) => setField('DS', v)}
                  {...getFieldProps('funcao-sistolica', 'DS')}
                />
                <CampoMedida
                  label="Fração de Encurtamento"
                  unit="%"
                  value={formData.fracaoEncurtamento}
                  editable={false}
                  onChange={(v) => setField('fracaoEncurtamento', v)}
                />
                <CampoMedida
                  label="Fração de Ejeção (Monoplanar)"
                  value={formData.fracaoEjecaoMono}
                  editable={false}
                  onChange={(v) => setField('fracaoEjecaoMono', v)}
                />
                <CampoMedida
                  label="Massa do VE"
                  value={formData.MASSAVE}
                  unit="g/m2"
                  editable
                  onChange={(v) => setField('MASSAVE', v)}
                  {...getFieldProps('funcao-sistolica', 'MASSAVE')}
                />
              </div>
              <div className="legacy-panel">
                <CampoMedida
                  label="Índice de Performance do VE (Normal < 0,28)"
                  value={formData.TEVE_NEW}
                  editable
                  onChange={(v) => setField('TEVE_NEW', v)}
                  {...getFieldProps('funcao-sistolica', 'TEVE_NEW')}
                />
                <CampoMedida
                  label="Estimativa do Índice Cardíaco"
                  value={formData.EDC_VE}
                  unit="l/min/m2 - Normal 2,6 a 4,2"
                  editable
                  onChange={(v) => setField('EDC_VE', v)}
                  {...getFieldProps('funcao-sistolica', 'EDC_VE')}
                />
                <CampoMedida
                  label="Cálculo da DPDT - (Normal > 1200)"
                  value={formData.DPDT}
                  editable
                  onChange={(v) => setField('DPDT', v)}
                  {...getFieldProps('funcao-sistolica', 'DPDT')}
                />
                <CampoMedida
                  label="Cálculo da FE (Método de Simpson)"
                  unit="%"
                  value={formData.FE}
                  editable
                  onChange={(v) => setField('FE', v)}
                  {...getFieldProps('funcao-sistolica', 'FE')}
                />
              </div>
            </div>
            <ListaDicionario
              title="Achados descritivos do VE"
              items={formData.achados}
              {...getListHandlers('achados')}
              {...getListProps('funcao-sistolica', 'achados')}
            />
            <ListaDicionarioComposta
              title="Contração segmentar"
              items={formData.contraRegiao}
              {...getCompositeListHandlers('contraRegiao', 'CONTRASEG')}
              {...getListProps('funcao-sistolica', 'contraRegiao')}
            />
          </>
        );
      case 'funcao-diastolica':
        return (
          <>
            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Velocidade E Mitral"
                  unit="cm/seg"
                  value={formData.VEM}
                  editable
                  onChange={(v) => setField('VEM', v)}
                  {...getFieldProps('funcao-diastolica', 'VEM')}
                />
                <CampoMedida
                  label="Velocidade A Mitral"
                  unit="cm/seg"
                  value={formData.VAM}
                  editable
                  onChange={(v) => setField('VAM', v)}
                  {...getFieldProps('funcao-diastolica', 'VAM')}
                />
                <CampoMedida
                  label="Relação E/A"
                  value={formData.relacaoEA}
                  editable={false}
                  onChange={(v) => setField('relacaoEA', v)}
                />
                <CampoMedida
                  label="TRIVVE"
                  unit="mseg"
                  value={formData.TRIVVE}
                  editable
                  onChange={(v) => setField('TRIVVE', v)}
                  {...getFieldProps('funcao-diastolica', 'TRIVVE')}
                />
                <CampoMedida
                  label="TDM"
                  unit="mseg"
                  value={formData.TDM}
                  editable
                  onChange={(v) => setField('TDM', v)}
                  {...getFieldProps('funcao-diastolica', 'TDM')}
                />
              </div>
              <div className="legacy-panel">
                <CampoMedida
                  label="Duração da Onda A Mitral"
                  unit="mseg"
                  value={formData.DOAM}
                  editable
                  onChange={(v) => setField('DOAM', v)}
                  {...getFieldProps('funcao-diastolica', 'DOAM')}
                />
                <CampoMedida
                  label="Duração da Onda A Veia Pulmonar"
                  unit="mseg"
                  value={formData.DOAVP}
                  editable
                  onChange={(v) => setField('DOAVP', v)}
                  {...getFieldProps('funcao-diastolica', 'DOAVP')}
                />
                <CampoMedida
                  label="Velocidade da Onda A Veia Pulmonar"
                  unit="mseg"
                  value={formData.VOAVP}
                  editable
                  onChange={(v) => setField('VOAVP', v)}
                  {...getFieldProps('funcao-diastolica', 'VOAVP')}
                />
                <CampoMedida
                  label="Onda E'"
                  value={formData.VTIAO}
                  editable
                  onChange={(v) => setField('VTIAO', v)}
                  {...getFieldProps('funcao-diastolica', 'VTIAO')}
                />
                <CampoMedida
                  label="Relação E/E'"
                  value={formData.AAD}
                  editable
                  onChange={(v) => setField('AAD', v)}
                  {...getFieldProps('funcao-diastolica', 'AAD')}
                />
              </div>
            </div>
            <ListaDicionario
              title="Comentários sobre a função diastólica"
              items={formData.avDiast}
              {...getListHandlers('avDiast')}
              {...getListProps('funcao-diastolica', 'avDiast')}
            />
          </>
        );
      case 'atrios':
        return (
          <>
            <CampoMedida
              label="Diâmetro do Átrio Esquerdo"
              unit="cm"
              value={formData.DAE}
              editable
              onChange={(v) => setField('DAE', v)}
              {...getFieldProps('atrios', 'DAE')}
            />
            <CampoMedida
              label="IVAE (Volume do Átrio Esquerdo)"
              unit="cm³ - Normal < 34 ml/m²"
              value={formData.VTIVSVE4}
              editable
              onChange={(v) => setField('VTIVSVE4', v)}
              {...getFieldProps('atrios', 'VTIVSVE4')}
            />
            <ListaDicionario
              title="Átrio Esquerdo"
              items={formData.atrioEsq}
              {...getListHandlers('atrioEsq')}
              {...getListProps('atrios', 'atrioEsq')}
            />
            <ListaDicionario
              title="Átrio Direito"
              items={formData.atrioDir}
              {...getListHandlers('atrioDir')}
              {...getListProps('atrios', 'atrioDir')}
            />
          </>
        );
      case 'ventriculo-aorta':
        return (
          <>
            <CampoMedida
              label="Diâmetro do Ventrículo Direito"
              unit="cm"
              value={formData.VA}
              editable
              onChange={(v) => setField('VA', v)}
              {...getFieldProps('ventriculo-aorta', 'VA')}
            />
            <ListaDicionario
              title="Ventrículo Direito"
              items={formData.ventriDir}
              {...getListHandlers('ventriDir')}
              {...getListProps('ventriculo-aorta', 'ventriDir')}
            />
            <CampoMedida
              label="Diâmetro da Aorta"
              unit="cm"
              value={formData.VTIVSVE1}
              editable
              onChange={(v) => setField('VTIVSVE1', v)}
              {...getFieldProps('ventriculo-aorta', 'VTIVSVE1')}
            />
            <ListaDicionario
              title="Aorta"
              items={formData.aorta}
              {...getListHandlers('aorta')}
              {...getListProps('ventriculo-aorta', 'aorta')}
            />
            {/*             <ListaDicionario
              title="Veia Cava Inferior"
              items={formData.veiaCavaInf}
              {...getListHandlers("veiaCavaInf")}
            /> */}
          </>
        );
      case 'valv-mitral':
        return (
          <>
            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Área Valva Mitral"
                  unit="cm²"
                  value={formData.AVM}
                  editable
                  onChange={(v) => setField('AVM', v)}
                  {...getFieldProps('valv-mitral', 'AVM')}
                />
                <CampoMedida
                  label="Gradiente Máximo"
                  unit="mmHg"
                  value={formData.VTIVSVE3}
                  editable
                  onChange={(v) => setField('VTIVSVE3', v)}
                  {...getFieldProps('valv-mitral', 'VTIVSVE3')}
                />
                <CampoMedida
                  label="Gradiente Médio"
                  unit="mmHg"
                  value={formData.GMVM}
                  editable
                  onChange={(v) => setField('GMVM', v)}
                  {...getFieldProps('valv-mitral', 'GMVM')}
                />
                <CampoMedida
                  label="Área de Refluxo IM Collor"
                  unit="cm²"
                  value={formData.ARIMCLL}
                  editable
                  onChange={(v) => setField('ARIMCLL', v)}
                  {...getFieldProps('valv-mitral', 'ARIMCLL')}
                />
                <CampoMedida
                  label="Área de AE"
                  unit="cm²"
                  value={formData.AAE}
                  editable
                  onChange={(v) => setField('AAE', v)}
                  {...getFieldProps('valv-mitral', 'AAE')}
                />
              </div>
              <div className="legacy-panel">
                <CampoMedida
                  label="Relação ao Collor IM/AE"
                  unit="cm²"
                  value={formData.relacaoIMAECLL}
                  editable={false}
                  onChange={(v) => setField('relacaoIMAECLL', v)}
                />
                <CampoMedida
                  label="Orifício Regurgitação Mitral (PISA)"
                  unit="cm²"
                  value={formData.ORIFICIO_VM}
                  editable
                  onChange={(v) => setField('ORIFICIO_VM', v)}
                  {...getFieldProps('valv-mitral', 'ORIFICIO_VM')}
                />
                <CampoMedida
                  label="Volume Regurgitação Mitral"
                  value={formData.VOLUME_VM}
                  unit="cc"
                  editable
                  onChange={(v) => setField('VOLUME_VM', v)}
                  {...getFieldProps('valv-mitral', 'VOLUME_VM')}
                />
                <CampoMedida
                  label="Fração de Regurgitação Mitral"
                  value={formData.VTIVSVE2}
                  unit="% - Normal/Trivial < 20%"
                  editable
                  onChange={(v) => setField('VTIVSVE2', v)}
                  {...getFieldProps('valv-mitral', 'VTIVSVE2')}
                />
              </div>
            </div>

            <ListaDicionario
              title="Valva Mitral"
              items={formData.valvaMitral}
              {...getListHandlers('valvaMitral')}
              {...getListProps('valv-mitral', 'valvaMitral')}
            />
            <ListaDicionario
              title="Refluxo da Valva Mitral"
              items={formData.refVM}
              {...getListHandlers('refVM')}
              {...getListProps('valv-mitral', 'refVM')}
            />
            <CampoMedida
              label="Gradiente Médio da Prótese Mitral"
              value={formData.GMPM}
              unit="mmHg"
              editable
              onChange={(v) => setField('GMPM', v)}
              {...getFieldProps('valv-mitral', 'GMPM')}
            />
            <ListaDicionarioComposta
              title="Refluxo da Prótese Mitral"
              items={formData.refluxoPM}
              {...getCompositeListHandlers('refluxoPM', 'refluxoPM')}
              {...getListProps('valv-mitral', 'refluxoPM')}
            />
          </>
        );
      case 'valv-aortica':
        return (
          <>
            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Área Valva Aórtica"
                  unit="cm²"
                  value={formData.AREA_AO}
                  editable
                  onChange={(v) => setField('AREA_AO', v)}
                  {...getFieldProps('valv-aortica', 'AREA_AO')}
                />
                <CampoMedida
                  label="Fração de Regurgitação Aórtica"
                  value={formData.VTIMITRAL2}
                  unit="%"
                  editable
                  onChange={(v) => setField('VTIMITRAL2', v)}
                  {...getFieldProps('valv-aortica', 'VTIMITRAL2')}
                />{' '}
                <CampoMedida
                  label="Gradiente Máximo da Valva Aórtica"
                  unit="mmHg"
                  value={formData.GMVSVE}
                  editable
                  onChange={(v) => setField('GMVSVE', v)}
                  {...getFieldProps('valv-aortica', 'GMVSVE')}
                />
                <CampoMedida
                  label="Gradiente Médio da Valva Aórtica"
                  value={formData.GMVAO}
                  unit="mmHg"
                  editable
                  onChange={(v) => setField('GMVAO', v)}
                  {...getFieldProps('valv-aortica', 'GMVAO')}
                />
              </div>
              <div className="legacy-panel">
                <CampoMedida
                  label="Diâmetro ao Collor I Aórtica"
                  value={formData.DIAOCLL}
                  unit="cm"
                  editable
                  onChange={(v) => setField('DIAOCLL', v)}
                  {...getFieldProps('valv-aortica', 'DIAOCLL')}
                />
                <CampoMedida
                  label="Relação ao Collor da I Aórtica"
                  value={formData.DAVAO}
                  unit="mmHg"
                  editable
                  onChange={(v) => setField('DAVAO', v)}
                  {...getFieldProps('valv-aortica', 'DAVAO')}
                />
                <CampoMedida
                  label="PHT da Valva Aórtica"
                  value={formData.VTIRM}
                  unit="mseg"
                  editable
                  onChange={(v) => setField('VTIRM', v)}
                  {...getFieldProps('valv-aortica', 'VTIRM')}
                />
              </div>
            </div>
            <ListaDicionario
              title="Valva Aórtica"
              items={formData.valvaAO}
              {...getListHandlers('valvaAO')}
              {...getListProps('valv-aortica', 'valvaAO')}
            />
            <ListaDicionario
              title="Refluxo da Valva Aórtica"
              items={formData.refVAO}
              {...getListHandlers('refVAO')}
              {...getListProps('valv-aortica', 'refVAO')}
            />
            <CampoMedida
              label="Gradiente Médio da Prótese Aórtica"
              value={formData.GMPAO}
              unit="mmHg"
              editable
              onChange={(v) => setField('GMPAO', v)}
              {...getFieldProps('valv-aortica', 'GMPAO')}
            />
            <CampoMedida
              label="Diâmetro da Via de Saída do VE"
              value={formData.DVSVE}
              unit="cm"
              editable
              onChange={(v) => setField('DVSVE', v)}
              {...getFieldProps('valv-aortica', 'DVSVE')}
            />{' '}
            <ListaDicionarioComposta
              title="Refluxo da Prótese Aórtica"
              items={formData.refluxoPAO}
              {...getCompositeListHandlers('refluxoPAO', 'refluxoPAO')}
              {...getListProps('valv-aortica', 'refluxoPAO')}
            />
          </>
        );
      case 'valv-tricuspide':
        return (
          <>
            <CampoMedida
              label="Gradiente Máximo da Valva Tricúspide"
              value={formData.GPIT}
              unit="mmHg"
              editable
              onChange={(v) => setField('GPIT', v)}
              {...getFieldProps('valv-tricuspide', 'GPIT')}
            />
            <CampoMedida
              label="Gradiente Médio da Valva Tricúspide"
              value={formData.GMVT}
              unit="mmHg"
              editable
              onChange={(v) => setField('GMVT', v)}
              {...getFieldProps('valv-tricuspide', 'GMVT')}
            />
            <CampoMedida
              label="Estimativa da Pressão Sistólica do VD"
              value={formData.RCIM}
              unit="mmHg"
              editable
              onChange={(v) => setField('RCIM', v)}
              {...getFieldProps('valv-tricuspide', 'RCIM')}
            />
            <ListaDicionario
              title="Valva Tricúspide"
              items={formData.valvaTricu}
              {...getListHandlers('valvaTricu')}
              {...getListProps('valv-tricuspide', 'valvaTricu')}
            />
            <ListaDicionario
              title="Refluxo da Valva Tricúspide"
              items={formData.refVT}
              {...getListHandlers('refVT')}
              {...getListProps('valv-tricuspide', 'refVT')}
            />
            <CampoMedida
              label="Gradiente Médio da Prótese Tricúspide"
              value={formData.GMPT}
              unit="mmHg"
              editable
              onChange={(v) => setField('GMPT', v)}
              {...getFieldProps('valv-tricuspide', 'GMPT')}
            />{' '}
            <ListaDicionarioComposta
              title="Refluxo da Prótese Tricúspide"
              items={formData.refluxoPT}
              {...getCompositeListHandlers('refluxoPT', 'refluxoPT')}
              {...getListProps('valv-tricuspide', 'refluxoPT')}
            />
          </>
        );
      case 'valv-pulmonar':
        return (
          <>
            <CampoMedida
              label="Diâmetro do Anel da Valva Pulmonar"
              value={formData.DAVP}
              unit="cm"
              editable
              onChange={(v) => setField('DAVP', v)}
              {...getFieldProps('valv-pulmonar', 'DAVP')}
            />
            <CampoMedida
              label="Gradiente Máximo da Valva Pulmonar"
              value={formData.VPRT}
              unit="mmHg"
              editable
              onChange={(v) => setField('VPRT', v)}
              {...getFieldProps('valv-pulmonar', 'VPRT')}
            />
            <CampoMedida
              label="Gradiente Médio da Valva Pulmonar"
              value={formData.GMVP}
              unit="mmHg"
              editable
              onChange={(v) => setField('GMVP', v)}
              {...getFieldProps('valv-pulmonar', 'GMVP')}
            />
            <ListaDicionario
              title="Valva Pulmonar"
              items={formData.valvaPulmo}
              {...getListHandlers('valvaPulmo')}
              {...getListProps('valv-pulmonar', 'valvaPulmo')}
            />
            <ListaDicionario
              title="Refluxo da Valva Pulmonar"
              items={formData.refVP}
              {...getListHandlers('refVP')}
              {...getListProps('valv-pulmonar', 'refVP')}
            />
            <CampoMedida
              label="Gradiente Máximo da Prótese Pulmonar"
              value={formData.GMPP}
              unit="mmHg"
              editable
              onChange={(v) => setField('GMPP', v)}
              {...getFieldProps('valv-pulmonar', 'GMPP')}
            />
            <ListaDicionarioComposta
              title="Refluxo da Prótese Pulmonar"
              items={formData.refluxoPP}
              {...getCompositeListHandlers('refluxoPP', 'refluxoPP')}
              {...getListProps('valv-pulmonar', 'refluxoPP')}
            />
          </>
        );
      case 'pericardio':
        return (
          <ListaDicionario
            title="Pericárdio"
            items={formData.pericardio}
            {...getListHandlers('pericardio')}
          />
        );
      case 'comentarios':
        return (
          <>
            <ListaDicionarioComposta
              title="Valvulopatias"
              items={formData.valvulopatia}
              {...getCompositeListHandlers('valvulopatia', 'valvulopatia')}
            />
            <ListaDicionarioComposta
              title="Próteses"
              items={formData.protese}
              {...getCompositeListHandlers('protese', 'protese')}
            />
            <ListaDicionarioComposta
              title="Contração Segmentar"
              items={formData.contraSeg}
              {...getCompositeListHandlers('contraSeg', 'contraSeg')}
            />
          </>
        );
      case 'observacoes':
        return (
          <>
            <ListaDicionarioComposta
              title="Conclusões"
              items={formData.conclusoes}
              {...getCompositeListHandlers('conclusoes', 'conclusoes')}
            />
            <Form.Group>
              <strong>Obs:</strong>
              <Form.Control
                as="textarea"
                rows={6}
                className="legacy-observacoes-area"
                value={formData.OBSERVACOES ?? ''}
                onChange={(e) => setField('OBSERVACOES', e.target.value)}
              />
            </Form.Group>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="mb-3 mt-2 no-print legacy-eco-card">
      <Card.Header className="legacy-header">
        <div className="legacy-header-side legacy-header-side-left">
          <div className="legacy-header-eco-wrap">
            <div className="legacy-mini-label">Eco Nº</div>
            <div className="legacy-mini-value">{eco?.CODECO || '-'}</div>
          </div>
          <span className="legacy-cloud-save-slot" aria-hidden={autoSaveUiState === 'idle'}>
            <AutoSaveCloudIndicator
              status={autoSaveUiState}
              className="legacy-cloud-save-header"
              compact
            />
          </span>
        </div>
        <div className="legacy-header-title-wrap text-center">
          <div className="legacy-header-title">Doppler</div>
        </div>
        <div className="legacy-header-side legacy-header-side-right">
          <div className="legacy-header-date-wrap">
            <div className="legacy-mini-label">Data</div>
            <div className="legacy-mini-value">{eco?.dataEcoFmt || '-'}</div>
          </div>
          <Button
            variant="outline-primary"
            className="legacy-dictionaries-btn"
            onClick={handleOpenDictionaryModal}
          >
            Dicionários
          </Button>
        </div>
      </Card.Header>
      {isLoading && (
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Card.Body>
      )}

      {error && !isLoading && (
        <Card.Body className="text-center text-danger py-5">
          {error.message || 'Erro ao carregar o laudo.'}
        </Card.Body>
      )}

      {eco && !isLoading && !error && (
        <>
          <Card.Body className="legacy-card-body">
            {/* <div className="legacy-toolbar">
              <button type="button" className="legacy-toolbar-btn">
                ◀◀
              </button>
              <button type="button" className="legacy-toolbar-btn">
                ◀
              </button>
              <button type="button" className="legacy-toolbar-btn">
                ▶
              </button>
              <button type="button" className="legacy-toolbar-btn">
                ▶▶
              </button>
              <button type="button" className="legacy-toolbar-btn">
                +
              </button>
              <button type="button" className="legacy-toolbar-btn">
                ⌃
              </button>
              <button
                type="button"
                className="legacy-toolbar-btn"
                onClick={handleGravar}
              >
                ✓
              </button>
              <button type="button" className="legacy-toolbar-btn">
                ✕
              </button>
              <button
                type="button"
                className="legacy-toolbar-btn"
                onClick={handleImprimir}
              >
                ↻
              </button>
            </div> */}

            <div className="legacy-content-shell container-fluid px-0">
              <Tab.Container activeKey={activeTab} onSelect={(k) => handleTabSelect(k)}>
                <Nav variant="pills" className="legacy-tabs-row legacy-tabs-row-bottom flex-wrap">
                  {bottomTabList.map(({ key, label }) => (
                    <Nav.Item key={key}>
                      <Nav.Link eventKey={key} className="legacy-tab-link">
                        {label}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <div className="legacy-section-title-wrap">
                  <div className="legacy-section-title">
                    {tabHeadingMap[activeTab] || 'Doppler'}
                  </div>
                  <div className="legacy-section-subtitle">
                    Preenchimento estruturado com auto-save e listas vinculadas a dicionários.
                  </div>
                </div>

                <Tab.Content className="legacy-tab-content">
                  {tabList.map(({ key, label }) => (
                    <Tab.Pane key={key} eventKey={key} title={label}>
                      {renderTabContent(key) || (
                        <p className="text-muted mb-0">Nenhum dado para &quot;{label}&quot;.</p>
                      )}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Tab.Container>
            </div>
          </Card.Body>
          <Card.Footer className="legacy-footer no-print d-flex flex-wrap justify-content-center gap-2">
            <Button variant="success" onClick={handleGravar} disabled={saving || autosaving}>
              {saving ? <Spinner animation="border" size="sm" /> : 'Gravar'}
            </Button>
            <Button variant="secondary" onClick={handleVisualizar}>
              Visualizar
            </Button>
            <Button variant="info" onClick={handleAnexar}>
              Anexar
            </Button>
          </Card.Footer>

          <Modal
            size="xl"
            show={showDictionaryModal}
            onHide={() => setShowDictionaryModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Dicionarios do Ecodoppler</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Tab.Container
                activeKey={activeDictionaryTab}
                onSelect={(k) => {
                  const next = k || activeDictionaryTab;
                  setActiveDictionaryTab(next);
                  loadDictionaryTab(next);
                }}
              >
                <Nav variant="tabs" className="legacy-dictionary-tabs">
                  {dictionaryTabList.map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <Tab.Content className="legacy-dictionary-content">
                  {dictionaryTabList.map((tab) => {
                    const list = Array.isArray(dictionaryData[tab.key])
                      ? dictionaryData[tab.key]
                      : [];
                    const draft = dictionaryDraft[tab.key] || {
                      codigo: '',
                      descricao: '',
                    };
                    const loading = dictionaryLoading[tab.key];

                    return (
                      <Tab.Pane key={tab.key} eventKey={tab.key}>
                        <div className="legacy-dictionary-new-row">
                          <Form.Control
                            type="text"
                            placeholder="Codigo"
                            value={draft.codigo}
                            onChange={(e) =>
                              setDictionaryDraft((prev) => ({
                                ...prev,
                                [tab.key]: {
                                  ...draft,
                                  codigo: e.target.value,
                                },
                              }))
                            }
                          />
                          <Form.Control
                            type="text"
                            placeholder="Descricao"
                            value={draft.descricao}
                            onChange={(e) =>
                              setDictionaryDraft((prev) => ({
                                ...prev,
                                [tab.key]: {
                                  ...draft,
                                  descricao: e.target.value,
                                },
                              }))
                            }
                          />
                          <Button
                            variant="success"
                            onClick={() => handleSaveDictionaryDraft(tab.key)}
                            disabled={savingDictionaryKey.startsWith(`${tab.key}:`)}
                          >
                            Incluir
                          </Button>
                        </div>

                        {loading ? (
                          <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : (
                          <div className="legacy-dictionary-grid">
                            {list.map((row, index) => {
                              const rowKey = `${tab.key}:${row.codigo}`;
                              return (
                                <div
                                  className="legacy-dictionary-row"
                                  key={rowKey || `${tab.key}-${index}`}
                                >
                                  <Form.Control
                                    type="text"
                                    value={row.codigo}
                                    onChange={(e) =>
                                      handleChangeDictionaryCell(
                                        tab.key,
                                        index,
                                        'codigo',
                                        e.target.value
                                      )
                                    }
                                  />
                                  <Form.Control
                                    type="text"
                                    value={row.descricao}
                                    onChange={(e) =>
                                      handleChangeDictionaryCell(
                                        tab.key,
                                        index,
                                        'descricao',
                                        e.target.value
                                      )
                                    }
                                  />
                                  <Button
                                    variant="primary"
                                    onClick={() => handleSaveDictionaryRow(tab.key, row)}
                                    disabled={savingDictionaryKey === rowKey}
                                  >
                                    Salvar
                                  </Button>
                                </div>
                              );
                            })}

                            {list.length === 0 ? (
                              <p className="text-muted mb-0">
                                Nenhum item encontrado neste dicionario.
                              </p>
                            ) : null}
                          </div>
                        )}
                      </Tab.Pane>
                    );
                  })}
                </Tab.Content>
              </Tab.Container>
            </Modal.Body>
          </Modal>
        </>
      )}
    </Card>
  );
}
